/**
 * DV-STOR-001: Stage Outputs Preview Component
 *
 * Preview artifacts from stage_outputs with URL verification and CORS handling
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileText,
  Image,
  Play,
  Download,
  ExternalLink,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Eye,
  RefreshCw,
  Grid3X3,
  List,
  Filter,
  Search,
  Copy,
  Square,
  RotateCcw,
} from 'lucide-react';
import { useStageOutputs } from '@/hooks/useStageOutputs';
import {
  verifyMultipleArtifacts,
  createVerificationLog,
  groupVerificationsByHost,
} from '@/lib/artifact-verification';
import type {
  StageOutput,
  ArtifactInfo,
  ArtifactVerification,
} from '@/types/stage-outputs';
import type { LogEntry } from './LogsPanel';
import { ErrorBanner, PermissionNotice, ServerErrorNotice, NotFoundNotice } from './ErrorNotices';
import { getDemoError } from '@/lib/demo-error-handler';
import { useStatusReport } from './useStatusReport';
import { stageOutputsAPIClient } from '@/hooks/useStageOutputs';
import { cn } from '@/utils/cn';

export interface StageOutputsPreviewProps {
  executionId: string;
  onLog?: (entry: LogEntry) => void;
  onErrorsOnlyChange?: (errorsOnly: boolean) => void;
  className?: string;
  'data-testid'?: string;
}

// Extract artifacts from stage output content
function extractArtifacts(output: StageOutput): ArtifactInfo[] {
  const artifacts: ArtifactInfo[] = [];

  // Check metadata for storage URLs
  if (output.metadata?.storage_url) {
    artifacts.push({
      name: `${output.stage}_storage`,
      type: 'url',
      url: output.metadata.storage_url,
      size: output.metadata.file_size,
    });
  }

  // Check content for various artifact types
  const content = output.content;

  // Text content (script, descriptions, etc.)
  Object.entries(content).forEach(([key, value]) => {
    if (typeof value === 'string' && value.length > 50) {
      artifacts.push({
        name: key,
        type: 'text',
        content: value,
        preview: value.substring(0, 200) + (value.length > 200 ? '...' : ''),
      });
    } else if (typeof value === 'object' && value !== null) {
      artifacts.push({
        name: key,
        type: 'json',
        content: JSON.stringify(value, null, 2),
        preview: JSON.stringify(value, null, 2).substring(0, 200) + '...',
      });
    }
  });

  // Look for URLs in content
  const urlPattern = /https?:\/\/[^\s<>"{}|\\^`[\]]+/g;
  const contentString = JSON.stringify(content);
  const urls = contentString.match(urlPattern);

  if (urls) {
    urls.forEach((url, index) => {
      // Skip if already added via metadata
      if (output.metadata?.storage_url === url) return;

      const type = getUrlType(url);
      artifacts.push({
        name: `url_${index + 1}`,
        type,
        url,
      });
    });
  }

  return artifacts;
}

// Determine URL type based on extension or patterns
function getUrlType(url: string): ArtifactInfo['type'] {
  const extension = url.split('.').pop()?.toLowerCase();

  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension || '')) {
    return 'image';
  } else if (['mp4', 'webm', 'mov', 'avi'].includes(extension || '')) {
    return 'video';
  } else if (['mp3', 'wav', 'ogg', 'm4a'].includes(extension || '')) {
    return 'audio';
  } else if (['json'].includes(extension || '')) {
    return 'json';
  }

  return 'url';
}

// Get appropriate icon for artifact type
function getArtifactIcon(type: ArtifactInfo['type']) {
  switch (type) {
    case 'text':
      return <FileText className="w-5 h-5" />;
    case 'json':
      return <FileText className="w-5 h-5 text-blue-400" />;
    case 'image':
      return <Image className="w-5 h-5 text-green-400" />;
    case 'video':
      return <Play className="w-5 h-5 text-purple-400" />;
    case 'audio':
      return <Play className="w-5 h-5 text-orange-400" />;
    case 'url':
      return <ExternalLink className="w-5 h-5 text-cyan-400" />;
    default:
      return <FileText className="w-5 h-5" />;
  }
}

// Get status badge for verification result
function getStatusBadge(verification?: ArtifactVerification) {
  if (!verification) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-gray-500/20 text-gray-300">
        <Clock className="w-3 h-3" />
        Pending
      </span>
    );
  }

  switch (verification.status) {
    case 'success':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-green-500/20 text-green-300">
          <CheckCircle className="w-3 h-3" />
          {verification.httpStatus || 'OK'}
        </span>
      );
    case 'failed':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-red-500/20 text-red-300">
          <XCircle className="w-3 h-3" />
          {verification.httpStatus || 'Failed'}
        </span>
      );
    case 'cors-error':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-yellow-500/20 text-yellow-300">
          <AlertTriangle className="w-3 h-3" />
          CORS
        </span>
      );
    case 'mixed-content':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-orange-500/20 text-orange-300">
          <AlertTriangle className="w-3 h-3" />
          Mixed Content
        </span>
      );
    case 'network-error':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-red-500/20 text-red-300">
          <XCircle className="w-3 h-3" />
          Network Error
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-gray-500/20 text-gray-300">
          <Clock className="w-3 h-3" />
          {verification.status}
        </span>
      );
  }
}

export const StageOutputsPreview: React.FC<StageOutputsPreviewProps> = ({
  executionId,
  onLog,
  onErrorsOnlyChange,
  className,
  'data-testid': testId = 'stage-outputs-preview',
}) => {
  const [selectedStage, setSelectedStage] = useState<string>('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterType, setFilterType] = useState<'all' | 'urls' | 'text' | 'media'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [verifications, setVerifications] = useState<Record<string, ArtifactVerification>>({});
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState<Error | null>(null);
  const [verificationMode, setVerificationMode] = useState<'current' | 'all'>('current');
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [errorsOnly, setErrorsOnly] = useState(false);

  // Handle errors only toggle
  const handleErrorsOnlyChange = useCallback((value: boolean) => {
    setErrorsOnly(value);
    onErrorsOnlyChange?.(value);
  }, [onErrorsOnlyChange]);

  // Fetch stage outputs
  const { data: stageData, isLoading, error } = useStageOutputs(executionId);

  // Log API call data when data is loaded
  useEffect(() => {
    if (stageData && onLog) {
      const recentApiCalls = stageOutputsAPIClient.getRecentApiCalls();
      const latestCall = recentApiCalls[0];

      if (latestCall) {
        onLog({
          id: `api-fetch-${Date.now()}`,
          ts: new Date().toISOString(),
          level: latestCall.status >= 400 ? 'error' : 'info',
          text: `Stage outputs API: ${latestCall.method} ${latestCall.path} (${latestCall.status})`,
          meta: {
            method: latestCall.method,
            path: latestCall.path,
            status: latestCall.status,
            elapsed: latestCall.elapsed,
            request_id: latestCall.requestId,
            total_outputs: stageData.total_outputs,
            total_stages: stageData.stages.length,
          },
        });
      }
    }
  }, [stageData, executionId, onLog]);

  // Status report functionality
  const { buildStageOutputsReport } = useStatusReport();

  const copyStatusReport = useCallback(async () => {
    if (!stageData) return;

    const recentApiCalls = stageOutputsAPIClient.getRecentApiCalls();
    const report = buildStageOutputsReport(
      stageData,
      verifications,
      [], // Recent logs - passed from parent component if needed
      recentApiCalls,
      executionId
    );

    try {
      await navigator.clipboard.writeText(report);
      onLog?.({
        id: `status-copied-${Date.now()}`,
        ts: new Date().toISOString(),
        level: 'info',
        text: 'Status report copied to clipboard',
        meta: { reportSize: report.length },
      });
    } catch (error) {
      onLog?.({
        id: `copy-failed-${Date.now()}`,
        ts: new Date().toISOString(),
        level: 'error',
        text: 'Failed to copy status report',
        meta: { error: error instanceof Error ? error.message : 'Unknown error' },
      });
    }
  }, [stageData, verifications, executionId, buildStageOutputsReport, onLog]);

  // Set default selected stage when data loads
  useEffect(() => {
    if (stageData && stageData.stages.length > 0 && !selectedStage) {
      setSelectedStage(stageData.stages[0]);
    }
  }, [stageData, selectedStage]);

  // Extract artifacts from selected stage
  const artifacts = useMemo(() => {
    if (!stageData || !selectedStage) return [];

    const stageOutputs = stageData.outputs_by_stage[selectedStage] || [];
    return stageOutputs.flatMap((output) =>
      extractArtifacts(output).map((artifact) => ({
        ...artifact,
        outputId: output.id,
        stageName: output.stage,
        createdAt: output.created_at,
      }))
    );
  }, [stageData, selectedStage]);

  // Filter artifacts based on search and type filter
  const filteredArtifacts = useMemo(() => {
    let filtered = artifacts;

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter((artifact) => {
        switch (filterType) {
          case 'urls':
            return artifact.type === 'url' || artifact.url;
          case 'text':
            return artifact.type === 'text' || artifact.type === 'json';
          case 'media':
            return ['image', 'video', 'audio'].includes(artifact.type);
          default:
            return true;
        }
      });
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (artifact) =>
          artifact.name.toLowerCase().includes(query) ||
          artifact.preview?.toLowerCase().includes(query) ||
          artifact.url?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [artifacts, filterType, searchQuery]);

  // Cancel verification
  const handleCancelVerification = useCallback(() => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setIsVerifying(false);
      onLog?.({
        id: `verify-cancelled-${Date.now()}`,
        ts: new Date().toISOString(),
        level: 'warn',
        text: 'URL verification cancelled by user',
        meta: { stage: selectedStage, execution: executionId },
      });
    }
  }, [abortController, selectedStage, executionId, onLog]);

  // Retry failed URLs only
  const handleRetryFailed = useCallback(async () => {
    if (!stageData || isVerifying) return;

    const failedUrls = Object.entries(verifications)
      .filter(([_, verification]) => verification.status !== 'success')
      .map(([url]) => url);

    if (failedUrls.length === 0) return;

    setIsVerifying(true);
    setVerificationError(null);

    const controller = new AbortController();
    setAbortController(controller);

    onLog?.({
      id: `retry-failed-${Date.now()}`,
      ts: new Date().toISOString(),
      level: 'info',
      text: `Retrying ${failedUrls.length} failed URLs`,
      meta: { stage: selectedStage, execution: executionId, failedCount: failedUrls.length },
    });

    try {
      const results = await verifyMultipleArtifacts(
        failedUrls,
        (completed, total, current) => {
          onLog?.({
            id: `retry-progress-${Date.now()}`,
            ts: new Date().toISOString(),
            level: 'info',
            text: `Retrying URLs: ${completed}/${total}`,
            meta: { current, progress: (completed / total) * 100 },
          });
        },
        {
          concurrency: 3,
          timeout: 10000,
          signal: controller.signal,
          enableThrottling: true
        }
      );

      setVerifications((prev) => ({ ...prev, ...results }));

      const successCount = Object.values(results).filter((r) => r.status === 'success').length;
      const stillFailedCount = Object.values(results).length - successCount;

      onLog?.({
        id: `retry-complete-${Date.now()}`,
        ts: new Date().toISOString(),
        level: stillFailedCount > 0 ? 'warn' : 'info',
        text: `Retry complete: ${successCount} now successful, ${stillFailedCount} still failed`,
        meta: { stage: selectedStage, successCount, stillFailedCount },
      });
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        onLog?.({
          id: `retry-cancelled-${Date.now()}`,
          ts: new Date().toISOString(),
          level: 'warn',
          text: 'Retry operation cancelled',
          meta: { stage: selectedStage, execution: executionId },
        });
      } else {
        const retryError = error instanceof Error ? error : new Error('Unknown retry error');
        setVerificationError(retryError);
        onLog?.({
          id: `retry-error-${Date.now()}`,
          ts: new Date().toISOString(),
          level: 'error',
          text: `Retry failed: ${retryError.message}`,
          meta: { stage: selectedStage, execution: executionId, error: retryError.message },
        });
      }
    } finally {
      setIsVerifying(false);
      setAbortController(null);
    }
  }, [stageData, isVerifying, verifications, selectedStage, executionId, onLog, abortController]);

  // Verify all URLs
  const handleVerifyUrls = useCallback(async () => {
    const urlArtifacts = artifacts.filter((artifact) => artifact.url);
    if (urlArtifacts.length === 0) return;

    setIsVerifying(true);
    setVerificationError(null);

    const controller = new AbortController();
    setAbortController(controller);

    const allArtifacts = verificationMode === 'all' && stageData
      ? stageData.stages.flatMap(stage =>
          stageData.outputs_by_stage[stage]?.flatMap(extractArtifacts) || []
        ).filter(artifact => artifact.url)
      : urlArtifacts;

    onLog?.({
      id: `verify-start-${Date.now()}`,
      ts: new Date().toISOString(),
      level: 'info',
      text: `Starting verification of ${allArtifacts.length} URLs (${verificationMode} mode)`,
      meta: { stage: selectedStage, execution: executionId, mode: verificationMode },
    });

    try {
      const urls = allArtifacts.map((artifact) => artifact.url!);

      const results = await verifyMultipleArtifacts(
        urls,
        (completed, total, current) => {
          onLog?.({
            id: `verify-progress-${Date.now()}`,
            ts: new Date().toISOString(),
            level: 'info',
            text: `Verifying URLs: ${completed}/${total}`,
            meta: { current, progress: (completed / total) * 100 },
          });
        },
        {
          concurrency: 3,
          timeout: 10000,
          signal: controller.signal,
          enableThrottling: true
        }
      );

      setVerifications((prev) => ({ ...prev, ...results }));

      // Log all verification results with enhanced metadata
      Object.entries(results).forEach(([url, verification]) => {
        const artifact = urlArtifacts.find((a) => a.url === url);
        if (artifact) {
          const logEntry = createVerificationLog(artifact.name, verification);
          onLog?.({
            id: logEntry.id,
            ts: logEntry.ts,
            level: logEntry.level,
            text: `${artifact.name}: ${logEntry.status} (${logEntry.responseTime}ms)`,
            meta: {
              url: logEntry.url,
              status: logEntry.status,
              hint: logEntry.hint,
              verification_method: verification.headers?.['x-verification-method'] || 'unknown',
              verification_flow: verification.headers?.['x-verification-flow'] || 'unknown',
              response_type: verification.headers?.['x-response-type'] || 'unknown',
              cors_supported: verification.corsSupported,
              ...logEntry.meta,
            },
          });
        }
      });

      const successCount = Object.values(results).filter((r) => r.status === 'success').length;
      const failedCount = Object.values(results).length - successCount;

      onLog?.({
        id: `verify-complete-${Date.now()}`,
        ts: new Date().toISOString(),
        level: failedCount > 0 ? 'warn' : 'info',
        text: `Verification complete: ${successCount} successful, ${failedCount} failed`,
        meta: { stage: selectedStage, successCount, failedCount },
      });
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        onLog?.({
          id: `verify-cancelled-${Date.now()}`,
          ts: new Date().toISOString(),
          level: 'warn',
          text: 'Verification cancelled by user',
          meta: { stage: selectedStage, execution: executionId },
        });
      } else {
        const verifyError = error instanceof Error ? error : new Error('Unknown verification error');
        setVerificationError(verifyError);
        onLog?.({
          id: `verify-error-${Date.now()}`,
          ts: new Date().toISOString(),
          level: 'error',
          text: `Verification failed: ${verifyError.message}`,
          meta: { stage: selectedStage, error: verifyError.message },
        });
      }
    } finally {
      setIsVerifying(false);
      setAbortController(null);
    }
  }, [artifacts, selectedStage, executionId, onLog, verificationMode, stageData]);

  // Auto-verify URLs when stage changes
  useEffect(() => {
    const urlArtifacts = artifacts.filter((artifact) => artifact.url);
    if (urlArtifacts.length > 0 && selectedStage) {
      // Check if we need to verify any URLs
      const needsVerification = urlArtifacts.some((artifact) => !verifications[artifact.url!]);
      if (needsVerification) {
        handleVerifyUrls();
      }
    }
  }, [selectedStage, artifacts, verifications, handleVerifyUrls]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8" data-testid={testId}>
        <div className="flex items-center gap-2 text-gray-400">
          <RefreshCw className="w-5 h-5 animate-spin" />
          Loading stage outputs...
        </div>
      </div>
    );
  }

  if (error) {
    const demoError = getDemoError(error);
    if (demoError) {
      const retryFetch = () => window.location.reload();

      return (
        <div data-testid={testId}>
          {demoError.type === 'permission_denied' && (
            <PermissionNotice
              error={demoError}
              onRetry={retryFetch}
              data-testid="stage-outputs-permission-error"
            />
          )}
          {demoError.type === 'not_found' && (
            <NotFoundNotice
              error={demoError}
              executionId={executionId}
              onRetry={retryFetch}
              data-testid="stage-outputs-not-found-error"
            />
          )}
          {demoError.type === 'server_error' && (
            <ServerErrorNotice
              error={demoError}
              onRetry={retryFetch}
              data-testid="stage-outputs-server-error"
            />
          )}
          {demoError.type === 'network_error' && (
            <ErrorBanner
              error={demoError}
              onRetry={retryFetch}
              data-testid="stage-outputs-network-error"
            />
          )}
        </div>
      );
    }
    // Fallback for non-demo errors
    return (
      <div className="p-6 border border-red-500/30 rounded-lg bg-red-900/10" data-testid={testId}>
        <div className="flex items-center gap-2 text-red-400 mb-2">
          <XCircle className="w-5 h-5" />
          Failed to load stage outputs
        </div>
        <p className="text-sm text-gray-400">
          {error instanceof Error ? error.message : 'Unknown error occurred'}
        </p>
      </div>
    );
  }

  if (!stageData || stageData.stages.length === 0) {
    return (
      <div className="p-6 text-center text-gray-400" data-testid={testId}>
        <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>No stage outputs found for execution {executionId}</p>
      </div>
    );
  }

  const urlArtifactsCount = artifacts.filter((artifact) => artifact.url).length;
  const verifiedCount = Object.keys(verifications).length;

  return (
    <div className={cn('space-y-6', className)} data-testid={testId}>
      {/* Header with stage selector and controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="space-y-2">
          <h3 className="text-lg font-medium text-white">Stage Outputs Preview</h3>
          <p className="text-sm text-gray-400">
            Execution: <code className="text-cyan-400">{executionId.slice(0, 8)}...</code> •{' '}
            {stageData.total_outputs} total outputs • {urlArtifactsCount} URLs
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Verification controls */}
          <div className="flex items-center gap-1">
            {!isVerifying ? (
              <>
                <button
                  onClick={handleVerifyUrls}
                  disabled={urlArtifactsCount === 0}
                  className="inline-flex items-center gap-2 px-3 py-1 text-sm rounded border border-cyan-500/30
                           bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 disabled:opacity-50 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Verify ({verifiedCount}/{urlArtifactsCount})
                </button>

                {Object.values(verifications).some(v => v.status !== 'success') && (
                  <button
                    onClick={handleRetryFailed}
                    disabled={!Object.values(verifications).some(v => v.status !== 'success')}
                    className="inline-flex items-center gap-2 px-3 py-1 text-sm rounded border border-orange-500/30
                             bg-orange-500/10 text-orange-300 hover:bg-orange-500/20 disabled:opacity-50 transition-colors"
                    title="Retry failed verifications only"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Retry Failed
                  </button>
                )}
              </>
            ) : (
              <button
                onClick={handleCancelVerification}
                className="inline-flex items-center gap-2 px-3 py-1 text-sm rounded border border-red-500/30
                         bg-red-500/10 text-red-300 hover:bg-red-500/20 transition-colors"
              >
                <Square className="w-4 h-4" />
                Stop
              </button>
            )}
          </div>

          {/* Verification mode toggle */}
          <div className="flex items-center border border-gray-600 rounded text-xs">
            <button
              onClick={() => setVerificationMode('current')}
              className={cn(
                'px-2 py-1',
                verificationMode === 'current' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'
              )}
              title="Verify URLs in current stage only"
            >
              Current
            </button>
            <button
              onClick={() => setVerificationMode('all')}
              className={cn(
                'px-2 py-1',
                verificationMode === 'all' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'
              )}
              title="Verify URLs in all stages"
            >
              All Stages
            </button>
          </div>

          <button
            onClick={copyStatusReport}
            disabled={!stageData}
            className="inline-flex items-center gap-2 px-3 py-1 text-sm rounded border border-gray-500/30
                     bg-gray-500/10 text-gray-300 hover:bg-gray-500/20 disabled:opacity-50 transition-colors"
            data-testid="dv-status-copy"
          >
            <Copy className="w-4 h-4" />
            Copy Status
          </button>

          <button
            onClick={() => handleErrorsOnlyChange(!errorsOnly)}
            className={cn(
              "inline-flex items-center gap-2 px-3 py-1 text-sm rounded border transition-colors",
              errorsOnly
                ? "border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                : "border-gray-500/30 bg-gray-500/10 text-gray-300 hover:bg-gray-500/20"
            )}
            title="Filter logs to show only errors, warnings, and verification failures"
          >
            <Filter className="w-4 h-4" />
            {errorsOnly ? 'Errors Only' : 'All Logs'}
          </button>

          <div className="flex items-center border border-gray-600 rounded">
            <button
              onClick={() => setViewMode('grid')}
              className={cn(
                'p-1 text-sm',
                viewMode === 'grid' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'
              )}
            >
              <Grid3X3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                'p-1 text-sm',
                viewMode === 'list' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'
              )}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Stage selector */}
      <div className="flex items-center gap-4">
        <label className="text-sm font-medium text-gray-300">Stage:</label>
        <select
          value={selectedStage}
          onChange={(e) => setSelectedStage(e.target.value)}
          className="px-3 py-1 bg-gray-800 border border-gray-600 rounded text-white text-sm
                   focus:border-cyan-500 focus:outline-none"
        >
          {stageData.stages.map((stage) => (
            <option key={stage} value={stage}>
              {stage} ({stageData.outputs_by_stage[stage]?.length || 0} outputs)
            </option>
          ))}
        </select>
      </div>

      {/* Enhanced Verification Summary Ribbon */}
      {urlArtifactsCount > 0 && (
        <div className={cn(
          "p-4 border rounded-lg transition-colors",
          Object.keys(verifications).length === 0
            ? "bg-blue-500/10 border-blue-500/30"
            : Object.values(verifications).every(v => v.status === 'success')
            ? "bg-green-500/10 border-green-500/30"
            : Object.values(verifications).some(v => ['cors-error', 'mixed-content', 'network-error', 'failed'].includes(v.status))
            ? "bg-red-500/10 border-red-500/30"
            : "bg-yellow-500/10 border-yellow-500/30"
        )}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <h4 className="text-sm font-medium text-white">URL Verification Status</h4>
              {isVerifying && (
                <div className="flex items-center gap-2 text-xs text-cyan-300">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Verifying...
                </div>
              )}
            </div>
            <div className="text-sm text-gray-300">
              {Object.keys(verifications).length}/{urlArtifactsCount} URLs verified
            </div>
          </div>

          {Object.keys(verifications).length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {(() => {
                const successCount = Object.values(verifications).filter(v => v.status === 'success').length;
                const corsCount = Object.values(verifications).filter(v => v.status === 'cors-error').length;
                const mixedContentCount = Object.values(verifications).filter(v => v.status === 'mixed-content').length;
                const networkErrorCount = Object.values(verifications).filter(v => v.status === 'network-error').length;
                const failedCount = Object.values(verifications).filter(v => v.status === 'failed').length;

                return (
                  <>
                    <div className="flex items-center gap-2 text-green-300">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-medium">{successCount}</span>
                      <span className="text-gray-400">successful</span>
                    </div>
                    <div className="flex items-center gap-2 text-yellow-300">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="font-medium">{corsCount}</span>
                      <span className="text-gray-400">CORS errors</span>
                    </div>
                    <div className="flex items-center gap-2 text-orange-300">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="font-medium">{mixedContentCount}</span>
                      <span className="text-gray-400">mixed content</span>
                    </div>
                    <div className="flex items-center gap-2 text-red-300">
                      <XCircle className="w-4 h-4" />
                      <span className="font-medium">{networkErrorCount + failedCount}</span>
                      <span className="text-gray-400">other failures</span>
                    </div>
                  </>
                );
              })()}
            </div>
          ) : (
            <div className="flex items-center justify-center text-gray-400 py-2">
              <div className="text-center">
                <Clock className="w-6 h-6 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  {urlArtifactsCount} URLs found • Click "Verify" to check accessibility
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Domain Insights */}
      {Object.keys(verifications).length > 0 && (() => {
        const hostStats = groupVerificationsByHost(verifications);
        if (hostStats.length === 0) return null;

        return (
          <div className="p-3 bg-gray-800/30 border border-gray-700 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-300">Domain Breakdown</h4>
              <div className="text-xs text-gray-400">
                {hostStats.length} domains
              </div>
            </div>
            <div className="space-y-2">
              {hostStats.slice(0, 5).map((host) => (
                <div key={host.hostname} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="text-cyan-300 truncate" title={host.hostname}>
                      {host.hostname}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({host.total} URLs)
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {host.success > 0 && (
                      <span className="inline-flex items-center gap-1 text-green-300 text-xs">
                        <CheckCircle className="w-3 h-3" />
                        {host.success}
                      </span>
                    )}
                    {host.corsError > 0 && (
                      <span className="inline-flex items-center gap-1 text-yellow-300 text-xs">
                        <AlertTriangle className="w-3 h-3" />
                        {host.corsError}
                      </span>
                    )}
                    {(host.forbidden + host.notFound + host.networkError + host.other) > 0 && (
                      <span className="inline-flex items-center gap-1 text-red-300 text-xs">
                        <XCircle className="w-3 h-3" />
                        {host.forbidden + host.notFound + host.networkError + host.other}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {hostStats.length > 5 && (
                <div className="text-xs text-gray-500 text-center pt-1">
                  +{hostStats.length - 5} more domains
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Filters and search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            className="px-3 py-1 bg-gray-800 border border-gray-600 rounded text-white text-sm
                     focus:border-cyan-500 focus:outline-none"
          >
            <option value="all">All Types</option>
            <option value="urls">URLs Only</option>
            <option value="text">Text/JSON</option>
            <option value="media">Media</option>
          </select>
        </div>

        <div className="flex items-center gap-2 flex-1 max-w-md">
          <Search className="w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search artifacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-3 py-1 bg-gray-800 border border-gray-600 rounded text-white text-sm
                     placeholder-gray-400 focus:border-cyan-500 focus:outline-none"
          />
        </div>
      </div>

      {/* Verification error notice */}
      {verificationError && (() => {
        const demoError = getDemoError(verificationError);
        return demoError ? (
          <ErrorBanner
            error={demoError}
            data-testid="verification-error"
            onDismiss={() => setVerificationError(null)}
          />
        ) : (
          <div className="p-4 border border-yellow-500/30 rounded-lg bg-yellow-900/10">
            <div className="flex items-center gap-2 text-yellow-400 mb-2">
              <AlertTriangle className="w-5 h-5" />
              URL verification failed
            </div>
            <p className="text-sm text-gray-400">{verificationError.message}</p>
            <button
              onClick={() => setVerificationError(null)}
              className="mt-2 text-xs text-yellow-300 hover:text-yellow-200"
            >
              Dismiss
            </button>
          </div>
        );
      })()}

      {/* Artifacts display */}
      {filteredArtifacts.length === 0 ? (
        <div className="p-6 text-center text-gray-400 border border-gray-700 rounded-lg">
          <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No artifacts found matching your criteria</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredArtifacts.map((artifact, index) => (
            <ArtifactCard
              key={`${artifact.outputId}-${index}`}
              artifact={artifact}
              verification={artifact.url ? verifications[artifact.url] : undefined}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredArtifacts.map((artifact, index) => (
            <ArtifactListItem
              key={`${artifact.outputId}-${index}`}
              artifact={artifact}
              verification={artifact.url ? verifications[artifact.url] : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Artifact card component for grid view
interface ArtifactCardProps {
  artifact: ArtifactInfo & { outputId?: string; stageName?: string; createdAt?: string };
  verification?: ArtifactVerification;
}

const ArtifactCard: React.FC<ArtifactCardProps> = ({ artifact, verification }) => {
  const [showPreview, setShowPreview] = useState(false);

  const handleView = () => {
    if (artifact.url) {
      window.open(artifact.url, '_blank', 'noopener,noreferrer');
    } else {
      setShowPreview(true);
    }
  };

  return (
    <div className="p-4 border border-gray-700 rounded-lg bg-gray-800/50 space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          {getArtifactIcon(artifact.type)}
          <span className="font-medium text-white truncate">{artifact.name}</span>
        </div>
        {artifact.url && getStatusBadge(verification)}
      </div>

      {artifact.preview && (
        <div className="text-xs text-gray-400 font-mono bg-gray-900/50 p-2 rounded max-h-20 overflow-hidden">
          {artifact.preview}
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>{artifact.type}</span>
        {artifact.size && <span>{(artifact.size / 1024).toFixed(1)} KB</span>}
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={handleView}
          className="flex items-center gap-1 px-2 py-1 text-xs border border-cyan-500/30
                   bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 rounded transition-colors"
        >
          {artifact.url ? <ExternalLink className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          {artifact.url ? 'Open' : 'View'}
        </button>
        {artifact.url && (
          <button
            onClick={() => window.open(artifact.url, '_blank')}
            className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-600
                     bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 rounded transition-colors"
          >
            <Download className="w-3 h-3" />
            Download
          </button>
        )}
      </div>

      {/* Preview modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
          <div className="max-w-4xl w-full max-h-[80vh] bg-gray-800 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="font-medium text-white">{artifact.name}</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-white"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 max-h-96 overflow-auto">
              <pre className="text-sm text-gray-300 whitespace-pre-wrap">
                {artifact.content}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Artifact list item component for list view
const ArtifactListItem: React.FC<ArtifactCardProps> = ({ artifact, verification }) => {
  return (
    <div className="flex items-center gap-4 p-3 border border-gray-700 rounded-lg bg-gray-800/30">
      <div className="flex items-center gap-2 flex-1">
        {getArtifactIcon(artifact.type)}
        <span className="font-medium text-white">{artifact.name}</span>
        <span className="text-xs text-gray-500">({artifact.type})</span>
      </div>

      <div className="flex items-center gap-4">
        {artifact.url && getStatusBadge(verification)}
        {artifact.size && (
          <span className="text-xs text-gray-500">{(artifact.size / 1024).toFixed(1)} KB</span>
        )}
        {verification?.responseTime && (
          <span className="text-xs text-gray-500">{verification.responseTime}ms</span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {artifact.url ? (
          <button
            onClick={() => window.open(artifact.url, '_blank')}
            className="flex items-center gap-1 px-2 py-1 text-xs border border-cyan-500/30
                     bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 rounded transition-colors"
          >
            <ExternalLink className="w-3 h-3" />
            Open
          </button>
        ) : (
          <button className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-600
                           bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 rounded transition-colors">
            <Eye className="w-3 h-3" />
            View
          </button>
        )}
      </div>
    </div>
  );
};